import founderArjun from "@/assets/founder-arjun.jpg";
import founderSarah from "@/assets/founder-sarah.jpg";
import founderDavid from "@/assets/founder-david.jpg";

export const services = [
  {
    title: "Bookkeeping",
    description:
      "Daily transaction recording, categorization, and a ledger that is always current and audit-ready.",
    points: ["Transaction categorization", "Chart of accounts hygiene"],
  },
  {
    title: "Accounting",
    description:
      "Accrual and cash-basis accounting managed by experienced global accounting professionals.",
    points: ["GAAP & IFRS-aligned entries", "Accruals and prepaids"],
  },
  {
    title: "Payroll Processing",
    description:
      "Full-cycle payroll including filings, benefit deductions, and contractor payments.",
    points: ["Global & multi-state payroll", "International contractor payments"],
  },
  {
    title: "Accounts Payable (AP)",
    description:
      "Vendor bills captured, approved, and paid on schedule so nothing slips past a due date.",
    points: ["Approval workflows", "Vendor management"],
  },
  {
    title: "Accounts Receivable (AR)",
    description:
      "Invoicing, collections follow-up, and aging control to shorten your cash conversion cycle.",
    points: ["Automated invoicing", "Collections follow-up"],
  },
  {
    title: "Bank & Card Reconciliations",
    description:
      "Every bank and credit card account reconciled on a fixed cadence with documented variances.",
    points: ["Monthly reconciliation", "Variance documentation"],
  },
  {
    title: "Month-End & Year-End Close",
    description:
      "A disciplined close calendar that delivers finished books within days, not weeks.",
    points: ["Close checklists", "CPA-ready workpapers"],
  },
  {
    title: "Financial Statements & Reporting",
    description:
      "P&L, balance sheet, and cash flow statements plus the management reporting your team acts on.",
    points: ["Management dashboards", "Budget vs. actual"],
  },
  {
    title: "CFO & Controller Support",
    description:
      "Senior financial oversight for forecasting, fundraising diligence, and internal controls.",
    points: ["Cash flow forecasting", "Internal controls"],
  },
  {
    title: "QuickBooks & Xero Management",
    description: "Setup, migration, and ongoing administration of your cloud accounting stack.",
    points: ["App integrations", "Workflow automation"],
  },
  {
    title: "Catch-up & Clean-up",
    description:
      "Months or years of backlogged books rebuilt, reconciled, and brought fully current.",
    points: ["Historical rebuilds", "Error correction"],
  },
  {
    title: "Finance Process Outsourcing",
    description:
      "A complete back-office function, documented and run as a dependable extension of your team.",
    points: ["Documented SOPs", "Dedicated finance pod"],
  },
];

export const industries = [
  { name: "Technology & SaaS", note: "MRR, ARR, deferred revenue and unit economics." },
  { name: "eCommerce", note: "Multi-channel sales, COGS, and inventory accuracy." },
  { name: "Healthcare", note: "Practice billing cycles and compliance-sensitive records." },
  { name: "Professional Services", note: "Project profitability and utilization reporting." },
  { name: "Marketing Agencies", note: "Retainers, pass-through media spend, and margins." },
  { name: "Construction", note: "Job costing, WIP schedules, and retainage tracking." },
  { name: "Real Estate", note: "Property-level P&Ls and investor reporting." },
  { name: "Manufacturing", note: "Cost accounting, BOMs, and inventory valuation." },
  { name: "Retail", note: "POS reconciliation and multi-location reporting." },
  { name: "Nonprofits", note: "Fund accounting, grants, and restricted balances." },
  { name: "Startups", note: "Investor-ready books and burn-rate visibility." },
  {
    name: "Small & Medium Businesses",
    note: "Complete back-office at a fraction of in-house cost.",
  },
];

export const differentiators = [
  {
    title: "Global accounting expertise",
    body: "Teams trained across international accounting standards (GAAP & IFRS), global tax frameworks, and multi-currency reporting.",
  },
  {
    title: "Dedicated professionals",
    body: "A named accountant and reviewer who know your business, not a rotating queue.",
  },
  {
    title: "Secure & confidential",
    body: "Access controls, encrypted document handling, and strict confidentiality protocols.",
  },
  {
    title: "Accurate, timely reporting",
    body: "Fixed close calendars so your statements arrive on the same date every month.",
  },
  {
    title: "Scalable solutions",
    body: "Capacity that expands with transaction volume without a new hiring cycle.",
  },
  {
    title: "Transparent communication",
    body: "Clear status updates, shared checklists, and responses within one business day.",
  },
  {
    title: "Cloud-based workflows",
    body: "QuickBooks, Xero, and modern automation replacing spreadsheets and email threads.",
  },
  {
    title: "Cost-effective",
    body: "A fully staffed finance function for materially less than an in-house team.",
  },
];

export const processSteps = [
  {
    step: "01",
    title: "Discovery & assessment",
    body: "We review your current books, systems, and reporting gaps, then scope the exact work required.",
  },
  {
    step: "02",
    title: "Onboarding & clean-up",
    body: "We migrate access, document your workflows, and bring historical records fully current.",
  },
  {
    step: "03",
    title: "Ongoing operations",
    body: "Your dedicated pod runs the monthly cycle and delivers reporting on a fixed calendar.",
  },
];

export const stats = [
  { value: 12, suffix: "+", label: "Startups & SMBs Supported", display: "12+" },
  { value: 100, suffix: "%", label: "Reconciled to Bank Feed", display: "100%" },
  { value: 5, suffix: "th", label: "Monthly Close by", display: "5th" },
  { value: 40, suffix: "%", label: "Avg Cost vs In-House", display: "40%" },
];

export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  author: string;
  company?: string;
  location?: string;
  avatar?: string;
  highlight?: string;
  metric?: string;
  stack?: string[];
  stage: string;
  plan: string;
  verified?: boolean;
};

export const testimonials: Testimonial[] = [
  {
    name: "Arjun Patel",
    role: "Co-Founder & CEO",
    company: "Hyperlayer AI (YC W24)",
    location: "San Francisco, CA",
    avatar: founderArjun,
    author: "Arjun Patel",
    metric: "48-Hour Seed Diligence",
    highlight: "Zero audit adjustments & clean ASC 606 revenue recognition",
    quote:
      "We incorporated in Delaware and signed with Finendra on day one. When our $2.8M seed round diligence kicked off, the lead investor's audit team requested 18 months of monthly waterfall schedules and revenue recognition models. Deepak's pod had everything reconciled in QuickBooks and Stripe down to the cent. Diligence wrapped in 48 hours without a single reclassification question. Easily the best financial decision we made as early-stage founders.",
    stack: ["QuickBooks Online", "Stripe Billing", "Mercury", "Gusto"],
    stage: "YC W24 • Seed ($4M ARR)",
    plan: "Starter → Growth",
    verified: true,
  },
  {
    name: "Sarah Lin",
    role: "Founder & COO",
    company: "Aura Living Co.",
    location: "Austin, TX",
    avatar: founderSarah,
    author: "Sarah Lin",
    metric: "14 States Sales Tax Compliant",
    highlight: "Automated COGS & 100% SKU-level gross margin visibility",
    quote:
      "Our previous bookkeeping agency completely choked on multi-channel Shopify payouts, payment gateway fees, and returns. Finendra rebuilt our entire chart of accounts, integrated A2X clearing accounts, and automated landed COGS tracking. For the first time, we know our true gross margins by SKU and our multi-state sales tax filings across 14 states run on autopilot without penalty notices.",
    stack: ["Shopify Plus", "A2X", "QuickBooks Online", "Bill.com"],
    stage: "Bootstrapped • $1.2M GMV",
    plan: "Growth",
    verified: true,
  },
  {
    name: "David M. Reynolds",
    role: "Director of Finance",
    company: "Biosphere Science Institute",
    location: "Boston, MA",
    avatar: founderDavid,
    author: "David M. Reynolds",
    metric: "Spotless Uniform Guidance Audit",
    highlight: "12 federal grant allocations with zero audit findings",
    quote:
      "Managing 12 federal and private research grants with strict indirect cost rate caps used to mean 60-hour crunch weeks before our annual audit. Finendra established segregated fund accounting schedules and monthly payroll allocation sheets that gave us our first spotless Single Audit (Uniform Guidance) in four years. They operate with the rigor of a Big-4 audit team.",
    stack: ["QuickBooks Desktop / QBO", "Bill.com", "Paylocity", "Excel Models"],
    stage: "Research 501(c)(3) • $6M Budget",
    plan: "Scale",
    verified: true,
  },
];

export const startupFaqs = [
  {
    q: "Why $199/month? How does this replace a full-time bookkeeper?",
    a: "Most small businesses have between 10 and 75 transactions a month. Hiring a full-time in-house bookkeeper at $60,000–$75,000/year ($5,000+/month plus benefits and taxes) means paying for 160 hours when you only need 5 to 10 hours of work. Our Essential plan ($199/mo) gives you a dedicated accounting professional who connects directly to your QuickBooks Online or Xero, reconciles your bank and credit card feeds every month, categorizes expenses, tracks 1099s, and delivers CPA-ready financials. You get 100% of the accuracy for a fraction of the cost.",
  },
  {
    q: "Who is the $199/month bookkeeping plan best suited for?",
    a: "It's built for US small businesses, single-member LLCs, solopreneurs, digital agencies, consultants, coaches, boutique eCommerce stores, and early-stage startups that generate up to 75 transactions/month and do not require a full-time in-house bookkeeper, but want spotless, CPA-ready financials without lifting a finger.",
  },
  {
    q: "I'm pre-revenue or early-stage. Do I need a bookkeeper?",
    a: "Yes. Setting up your chart of accounts and separating business from personal finances from day one prevents costly cleanup later. When you file taxes or pitch investors, having clean, reconciliation-backed books makes everything painless. Our $199/mo plan is specifically priced so early-stage businesses can afford institutional-grade books.",
  },
  {
    q: "Can I pause or cancel if my business needs change?",
    a: "Absolutely. Finendra operates on month-to-month retainers with no multi-year contracts or cancellation fees. Give us 30 days' notice. Your books remain clean, organized, and handover-ready in your cloud software.",
  },
  {
    q: "What's the difference between Essential ($199) and Growth ($499)?",
    a: "Essential ($199/mo) is cash-basis bookkeeping and monthly reporting for businesses with up to 75 txns/mo that don't need a full-time bookkeeper. Growth ($499/mo) handles up to 250 txns, multiple merchant feeds, accrual or modified cash accounting, monthly close by the 5th, AP bill pay, AR invoicing, multi-state payroll, and dedicated monthly strategy reviews.",
  },
  {
    q: "Do you work inside our existing QuickBooks Online or Xero?",
    a: "Yes, 100%. We work directly inside your QuickBooks Online or Xero account, connected with your bank feeds (Mercury, Chase, Brex, Ramp, Bank of America, Wells Fargo, etc.) and payment processors (Stripe, PayPal, Shopify). You retain total ownership of your files.",
  },
  {
    q: "Can you work with my existing CPA for tax filing?",
    a: "Yes, seamlessly. We prepare GAAP-compliant trial balances, balance sheet reconciliations, and tax workpapers (Form 1040 Schedule C, 1120, 1120-S support) so your CPA can complete your tax return quickly without needing to reclassify messy transactions.",
  },
  {
    q: "How do you handle ASC 606 / revenue recognition for SaaS?",
    a: "For our Scale and Custom tiers, we maintain deferred revenue waterfall schedules, recognize subscription revenue ratably over service terms, record contract assets/liabilities, and reconcile Stripe billing directly to the general ledger.",
  },
  {
    q: "What about Form 5472 / 1120-F for foreign-owned US corps?",
    a: "If your US entity is 25%+ owned by a non-US individual or foreign parent (common with cross-border India, UK, and Singapore founders), we track all intercompany reportable transactions, loan transfers, and prepare audit-ready workpapers for Form 5472 compliance.",
  },
];

export const faqs = [
  ...startupFaqs,
  {
    q: "How quickly can we get started?",
    a: "Most engagements begin within 24 to 48 hours. After a quick scoping call, we connect read-only access to your accounting and banking portals, verify your feeds, and start bookkeeping immediately.",
  },
  {
    q: "Do you work inside our existing accounting software?",
    a: "Yes. We work directly inside your QuickBooks Online, Xero, NetSuite, Gusto, Rippling, Stripe, Brex, Ramp, or Bill.com accounts. You retain 100% ownership of your accounts and financial data.",
  },
  {
    q: "What if our books are months or years behind?",
    a: "Historical catch-up and cleanup is our specialty. We rebuild neglected ledgers, reconcile every bank and credit card statement, correct miscategorized expenses, and deliver an audit-ready balance sheet.",
  },
  {
    q: "How do you protect our financial data?",
    a: "We enforce role-based access, 2FA/MFA on all internal platforms, TLS 1.3 in transit, AES-256 encryption at rest, and strict employee NDAs and Data Protection Agreements (DPAs).",
  },
];

export type PricingFaqGroup = {
  title: string;
  items: { q: string; a: string }[];
};

export const pricingFaqs: PricingFaqGroup[] = [
  {
    title: "Essential Bookkeeping & Small Business FAQ",
    items: startupFaqs.slice(0, 5),
  },
  {
    title: "What's included & Deliverables",
    items: [
      {
        q: "What do I actually get with the $199/mo Essential plan?",
        a: "A dedicated human bookkeeper who reconciles all bank and credit card accounts every month, categorizes all business expenses and revenue, keeps your Chart of Accounts pristine, prepares monthly P&L and Balance Sheet reports, and tracks 1099 vendors so you're 100% tax-ready.",
      },
      {
        q: "Are reviews and quality checks included?",
        a: "Yes. Even on our $199/mo plan, every month's reconciliations are reviewed by a senior controller before deliverables are finalized.",
      },
      {
        q: "Is software or subscription cost included?",
        a: "QuickBooks, Xero, Gusto, and bank fees remain billed to you directly so you always own your data, history, and licenses.",
      },
    ],
  },
  {
    title: "Billing & Contracts",
    items: [
      {
        q: "When am I billed?",
        a: "Monthly plans are billed in advance on the 1st of each month. No long-term contracts — cancel or upgrade with 30 days' notice.",
      },
      {
        q: "Can I upgrade or downgrade as transaction volume changes?",
        a: "Yes. Move between Essential ($199), Growth ($499), and Scale ($1,299) seamlessly as your transaction volume and operational complexity evolve.",
      },
      {
        q: "How do payments work?",
        a: "We accept US ACH bank transfer, international wire, and major corporate credit cards (Brex, Ramp, Visa, Mastercard).",
      },
    ],
  },
];

export type PricingPlan = {
  badge?: string;
  name: string;
  price: number | null;
  interval: string;
  monthly?: string;
  hourly?: string;
  originalMonthly?: string;
  customLabel?: string;
  bestFor: string;
  includes: string[];
  features?: string[];
  cta: string;
  href: string;
  popular?: boolean;
  featured?: boolean;
  note?: string;
  variant?: string;
};

export const pricingPlans: PricingPlan[] = [
  {
    name: "Essential",
    price: 199,
    interval: "mo",
    monthly: "From $199",
    hourly: "$25",
    badge: "No Full-Time Needed",
    bestFor: "Businesses that don't need a full-time bookkeeper • Solos, LLCs & SMBs • Up to 75 txns/mo",
    includes: [
      "Monthly bank & credit card account reconciliations",
      "Transaction categorization in QuickBooks Online or Xero",
      "Monthly Profit & Loss (P&L) + Balance Sheet",
      "Chart of accounts setup & maintenance",
      "Year-end 1099 vendor tracking & CPA tax packet",
      "Dedicated human bookkeeper with Controller review",
      "Email support with 24–48hr response time",
      "Up to 75 transactions per month",
    ],
    features: [
      "Monthly bank & credit card reconciliations",
      "Categorized transactions in QuickBooks / Xero",
      "Monthly P&L and Balance Sheet reports",
      "1099 vendor tracking & tax-ready workpapers",
      "Dedicated bookkeeper with Controller review",
      "Up to 75 transactions per month",
    ],
    cta: "Start from $199/mo",
    href: "/contact?plan=essential",
    note: "Starting from $199/mo • Saves $60,000+/year vs hiring full-time in-house",
  },
  {
    name: "Growth",
    price: 499,
    interval: "mo",
    monthly: "From $499",
    hourly: "$40",
    badge: "Most Popular",
    popular: true,
    featured: true,
    bestFor: "$20K–$100K/mo revenue • Expanding SMBs & venture startups • Up to 250 txns/mo",
    includes: [
      "Everything in Essential (from $199/mo), plus:",
      "Accrual or modified cash accounting + close by 5th",
      "Up to 250 txns & multiple merchant feeds (Stripe/Shopify)",
      "Accounts Payable (AP) bill pay & AR invoicing support",
      "Payroll reconciliation (Gusto, Rippling, ADP) & 1099 filings",
      "Sales tax nexus monitoring (up to 5 states)",
      "Cash flow summary & monthly strategy review call",
    ],
    features: [
      "Accrual accounting & close by 5th business day",
      "Up to 250 txns & multi-account reconciliations",
      "AP bill pay & AR invoicing workflows",
      "Payroll execution (Gusto/Rippling) & 1099 filings",
      "Sales tax nexus tracking & filings",
      "Dedicated accountant + monthly review call",
    ],
    cta: "Book Growth Diagnostic",
    href: "/contact?plan=growth",
    note: "From $499/mo • Covers 90% of growing businesses with active payroll & billing",
  },
  {
    name: "Scale",
    price: 1299,
    interval: "mo",
    monthly: "From $1,299",
    hourly: "$65",
    badge: "Controller & Scale",
    bestFor: "$100K–$300K/mo revenue • Multi-state • ASC 606 revenue recognition",
    includes: [
      "Everything in Growth (from $499/mo), plus:",
      "Dedicated Controller oversight & board-ready financial pack",
      "ASC 606 deferred revenue waterfall & contract rev rec",
      "Department-level P&L (Eng, Sales, Marketing, G&A)",
      "Multi-state sales tax filing (10+ states) + economic nexus",
      "Burn rate, runway, and cash flow forecasting",
      "Big-4 audit-ready workpaper preparation",
    ],
    features: [
      "Dedicated Controller oversight & board decks",
      "ASC 606 deferred revenue waterfall modeling",
      "Departmental P&L (Eng, G&A, Sales, Marketing)",
      "Multi-state economic nexus & sales tax filings",
      "Burn rate, cash runway & financial forecasting",
      "Big 4 audit-ready workpaper preparation",
    ],
    cta: "Book Scale Diagnostic",
    href: "/contact?plan=scale",
    note: "Institutional finance for venture-backed and high-scale companies",
  },
  {
    name: "Custom",
    price: null,
    interval: "mo",
    hourly: "$100",
    badge: "Enterprise",
    bestFor: "$300K+/mo revenue • Multi-entity • ERP • Dedicated pod",
    includes: [
      "Everything in Scale, plus:",
      "NetSuite / Sage Intacct ERP setup & management",
      "Multi-country consolidation (US + UK + India + more)",
      "Transfer pricing documentation & Form 5472 compliance",
      "Named Controller + dedicated 2-person finance pod",
      "Weekly close option & real-time custom dashboards",
      "Board meeting prep & strategic finance modeling",
    ],
    features: [
      "NetSuite / Sage Intacct ERP management",
      "Cross-border consolidation (US, UK, India)",
      "Transfer pricing & cross-border compliance",
      "Named controller + dedicated finance pod",
      "Weekly close option & custom real-time metrics",
      "Board meeting prep & presentation support",
    ],
    cta: "Discuss Custom Plan",
    href: "/contact?plan=custom",
    variant: "outline",
  },
];

/** Value shown in a comparison cell: true = included, false = not included, string = detail. */
export type ComparisonValue = boolean | string;

export type ComparisonRow = {
  label: string;
  hint?: string;
  /** Values in plan order: Essential, Growth, Scale, Custom. */
  values: [ComparisonValue, ComparisonValue, ComparisonValue, ComparisonValue];
};

export type ComparisonGroup = {
  title: string;
  rows: ComparisonRow[];
};

export const pricingComparison: ComparisonGroup[] = [
  {
    title: "Fit & Engagement Model",
    rows: [
      {
        label: "Best for revenue stage",
        values: [
          "No full-time needed ($0–$25K/mo)",
          "$25K–$100K/mo revenue",
          "$100K–$300K/mo revenue",
          "$300K+/mo revenue",
        ],
      },
      {
        label: "Monthly investment",
        values: ["From $199 /mo", "From $499 /mo", "From $1,299 /mo", "Custom"],
      },
      {
        label: "Full-time hire savings",
        hint: "Versus hiring an in-house US bookkeeper ($65,000+/year salary + benefits)",
        values: ["Save ~$62,000+/yr", "Save ~$59,000+/yr", "Save ~$50,000+/yr", "Dedicated Team"],
      },
      {
        label: "Accounting basis",
        values: [
          "Cash basis",
          "Accrual or modified cash",
          "Accrual (ASC 606 rev rec)",
          "Full GAAP / Multi-entity",
        ],
      },
      {
        label: "Close cadence",
        values: ["Monthly by 10th", "5th of each month", "5th of each month", "Weekly option"],
      },
      {
        label: "Dedicated bookkeeper",
        hint: "A named human accounting professional who knows your accounts.",
        values: [true, true, true, true],
      },
      {
        label: "Senior controller oversight",
        values: ["Monthly check", "Monthly lead review", "Dedicated Controller", "Controller & Pod"],
      },
    ],
  },
  {
    title: "Bookkeeping & Operations",
    rows: [
      {
        label: "Monthly transaction volume",
        values: ["Up to 75 txns", "Up to 250 txns", "Up to 1,000 txns", "Unlimited custom"],
      },
      { label: "Bank & card reconciliations", values: [true, true, true, true] },
      { label: "Chart of accounts hygiene", values: [true, true, true, true] },
      { label: "Tax-ready annual workpapers & 1099 tracking", values: [true, true, true, true] },
      { label: "Accounts payable & bill pay", values: [false, true, true, true] },
      { label: "Accounts receivable & collections", values: [false, true, true, true] },
      { label: "Payroll processing (Gusto/Rippling) & 1099s", values: [false, true, true, true] },
      {
        label: "Sales tax nexus monitoring",
        values: [false, "Up to 5 states", "15+ states + filing", "Global nexus"],
      },
      { label: "Multi-entity & intercompany consolidation", values: [false, false, true, true] },
    ],
  },
  {
    title: "Reporting & Strategic Finance",
    rows: [
      { label: "Monthly Profit & Loss and Balance Sheet", values: [true, true, true, true] },
      { label: "Monthly board-ready reporting deck", values: [false, true, true, true] },
      { label: "Burn rate & cash runway forecasting", values: [false, true, true, true] },
      { label: "Department-level P&L (Eng, Sales, Mktg)", values: [false, false, true, true] },
      { label: "Custom KPI dashboard (CAC, LTV, NRR)", values: [false, false, true, true] },
      { label: "Big 4 audit-ready workpapers", values: [false, false, true, true] },
      {
        label: "Board meeting attendance & fundraising models",
        values: [false, false, false, true],
      },
    ],
  },
];
